// src/features/leases/leaseSwitcherUtils.js
//
// LSE-LEASE-TAB-AREAS-AND-SWITCHER — pure helpers for the multi-lease switcher.
// Exported for unit tests; no side-effects.
//
// Default-primary selection rule (from brief):
//   1. Active leases the tenant holds (direct_lease, sublease_inbound, serviced_office)
//      → if multiple, prefer direct_lease; within a type prefer largest `size`
//   2. Active sublease_outbound (Ripple is landlord; pick largest)
//   3. Any active lease
//   4. Most-recent non-active lease (by end_date DESC, then created_at DESC)
//
// Grouping for the switcher strip:
//   "Your space"          — active, type ∈ {direct_lease, sublease_inbound, serviced_office}
//   "Subleased out"       — active, type = sublease_outbound
//   "Past leases"         — non-active (expired, terminated, holdover, in_negotiation,
//                           sublease_out, draft)

// ─── Constants ────────────────────────────────────────────────────────────────

import { leaseAreaSummary } from "./leaseAreaSummary.js";
import { leaseAreaPresentation } from "./leaseAreaPresentation.js";

const YOUR_SPACE_TYPES = new Set(["direct_lease", "sublease_inbound", "serviced_office"]);

const TYPE_PRIORITY = {
  direct_lease:      0,
  sublease_inbound:  1,
  serviced_office:   2,
  sublease_outbound: 3,
};

// ─── Primary-lease selection ──────────────────────────────────────────────────

/**
 * Selects the "primary" lease from a list of all location leases.
 * Returns null when the list is empty.
 *
 * Selection rule:
 *   1. Active tenant-held lease (direct_lease > sublease_inbound > serviced_office);
 *      within same type, largest `size` wins (null size treated as 0).
 *   2. Active sublease_outbound (largest size).
 *   3. Any other active lease.
 *   4. Most-recent non-active lease.
 *
 * @param {Array} leases  Array of lease objects from listLocationLeases().
 * @returns {object|null}
 */
export function selectPrimaryLease(leases) {
  if (!Array.isArray(leases) || leases.length === 0) return null;

  const active = leases.filter((l) => l.status === "active");

  if (active.length > 0) {
    const tenantHeld = active.filter((l) => YOUR_SPACE_TYPES.has(l.type));
    if (tenantHeld.length > 0) {
      return tenantHeld.reduce((best, l) => {
        const bPriority = TYPE_PRIORITY[best.type] ?? 99;
        const lPriority = TYPE_PRIORITY[l.type]    ?? 99;
        if (lPriority < bPriority) return l;
        if (lPriority > bPriority) return best;
        // Same type — relocation-aware tiebreaking:
        //   1. Prefer the in-build lease (new space being prepared) over the
        //      existing occupied space.
        //   2. If both or neither are in-build, prefer the larger footprint.
        const lInBuild = Array.isArray(l.areas)    && l.areas.some((a) => a.isInBuild);
        const bInBuild = Array.isArray(best.areas) && best.areas.some((a) => a.isInBuild);
        if (lInBuild && !bInBuild) return l;
        if (!lInBuild && bInBuild) return best;
        return (Number(l.size) || 0) >= (Number(best.size) || 0) ? l : best;
      });
    }
    const outbound = active.filter((l) => l.type === "sublease_outbound");
    if (outbound.length > 0) {
      return outbound.reduce((best, l) =>
        (Number(l.size) || 0) >= (Number(best.size) || 0) ? l : best
      );
    }
    return active[0];
  }

  return leases.reduce((best, l) => {
    const bEnd = best.endDate ? new Date(best.endDate).getTime() : 0;
    const lEnd = l.endDate   ? new Date(l.endDate).getTime()   : 0;
    if (lEnd !== bEnd) return lEnd > bEnd ? l : best;
    const bCr = best.createdAt ? new Date(best.createdAt).getTime() : 0;
    const lCr = l.createdAt   ? new Date(l.createdAt).getTime()   : 0;
    return lCr > bCr ? l : best;
  });
}

// ─── Switcher grouping ────────────────────────────────────────────────────────

/**
 * Groups leases into the three switcher sections.
 * Each section is omitted (empty array) when it has no members.
 *
 * @param {Array} leases
 * @returns {{ yourSpace: Array, subleaseOut: Array, past: Array }}
 */
export function groupLeasesForSwitcher(leases) {
  if (!Array.isArray(leases)) return { yourSpace: [], subleaseOut: [], past: [] };

  const yourSpace   = [];
  const subleaseOut = [];
  const past        = [];

  for (const l of leases) {
    if (l.status === "active") {
      if (l.type === "sublease_outbound") {
        subleaseOut.push(l);
      } else {
        yourSpace.push(l);
      }
    } else {
      past.push(l);
    }
  }

  return { yourSpace, subleaseOut, past };
}

// ─── Display helpers ──────────────────────────────────────────────────────────

/**
 * Build the short floor/area summary label for a switcher entry.
 * Prefers structured areas names; falls back to lease.areas text; then "—".
 *
 * @param {object} lease  Lease row (with optional `areas` array).
 * @returns {string}
 */
export function leaseAreaSummaryLabel(lease) {
  if (Array.isArray(lease.areas) && lease.areas.length > 0) {
    const { active, expiredCount } = leaseAreaPresentation(lease);
    const names = active.map((area) => area.name?.trim()).filter(Boolean);
    return names.length ? `${names.join(" / ")}${expiredCount ? ` (+${expiredCount} expired)` : ""}`
      : leaseAreaSummary(lease);
  }
  if (lease.areas && typeof lease.areas === "string" && lease.areas.trim()) {
    return lease.areas.trim();
  }
  return "—";
}
